import React, { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { pmApi, type Task, type WorkstreamState, type Phase } from './pmApi';
import { lockedDescription } from './sourceInfo';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../../components/shadcn/ui/dialog';
import { Button } from '../../../components/shadcn/ui/button';
import { Input } from '../../../components/shadcn/ui/input';
import { Label } from '../../../components/shadcn/ui/label';
import { Textarea } from '../../../components/shadcn/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/shadcn/ui/select';

/**
 * Add to / edit the plan.
 *
 * Provenance is enforced here as well as on the server: a task that came from
 * the master-plan export has its source fields (dates, status, owner) locked,
 * because the spreadsheet is still the system of record for those at this
 * migration stage and the next import would silently revert an edit made here.
 * Execution fields — next action, advancement criterion — are always editable,
 * since those belong to leadership rather than to the export.
 */

const STATUSES = ['Not Started', 'Working on it', 'Stuck', 'Done'];
const PRIORITIES = ['Critical', 'Important', 'Standard'];

export default function TaskDialog({
  open, onOpenChange, task, workstreams, owners, defaultWorkstreamId, onSaved,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  task?: Task | null;
  workstreams: WorkstreamState[];
  owners: string[];
  defaultWorkstreamId?: string;
  onSaved: () => void;
}) {
  const isEdit = Boolean(task);
  // Locks on the SAME value the server enforces (external_source), so the form
  // can never offer an edit the API will then reject.
  const locked = isEdit && task?.externalSource !== 'manual';

  const [name, setName] = useState('');
  const [workstreamId, setWorkstreamId] = useState('');
  const [phaseId, setPhaseId] = useState('');
  const [ownerRole, setOwnerRole] = useState('');
  const [status, setStatus] = useState('Not Started');
  const [priority, setPriority] = useState('Important');
  const [startDate, setStartDate] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [effortDays, setEffortDays] = useState('');
  const [notes, setNotes] = useState('');
  const [nextAction, setNextAction] = useState('');
  const [advancement, setAdvancement] = useState('');
  const [phases, setPhases] = useState<Phase[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset the form whenever the dialog opens for a different subject.
  useEffect(() => {
    if (!open) return;
    setError(null);
    setName(task?.name || '');
    setWorkstreamId(task?.workstreamId || defaultWorkstreamId || '');
    setOwnerRole(task?.owner || '');
    setStatus(task?.status || 'Not Started');
    setPriority(task?.priority || 'Important');
    setStartDate(task?.startDate || '');
    setDueDate(task?.dueDate || '');
    setEffortDays(task?.effortDays != null ? String(task.effortDays) : '');
    setNotes(task?.notes || '');
    setNextAction(task?.nextAction || '');
    setAdvancement(task?.advancementCriterion || '');
    setPhaseId('');
  }, [open, task, defaultWorkstreamId]);

  useEffect(() => {
    if (!workstreamId) { setPhases([]); return; }
    pmApi.getPhases(workstreamId).then((r) => {
      if (r.success && r.data) setPhases(r.data);
    });
  }, [workstreamId]);

  const canSave = useMemo(
    () => (locked ? true : Boolean(name.trim() && workstreamId)),
    [locked, name, workstreamId]
  );

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      if (isEdit && task) {
        // Locked tasks accept execution fields only — matching the server rule.
        const body: Record<string, unknown> = {
          next_action: nextAction.trim() || null,
          advancement_criterion: advancement.trim() || null,
        };
        if (!locked) {
          Object.assign(body, {
            name: name.trim(),
            workstream_id: workstreamId,
            phase_id: phaseId || null,
            status,
            priority,
            start_date: startDate || null,
            due_date: dueDate || null,
            effort_days: effortDays ? Number(effortDays) : null,
            notes: notes.trim() || null,
          });
        }
        const res = await pmApi.updateTask(task.id, body);
        if (!res.success) throw new Error(res.error || res.message || 'Could not save.');
      } else {
        const res = await pmApi.createTask({
          name: name.trim(),
          workstreamId,
          phaseId: phaseId || undefined,
          ownerRole: ownerRole || undefined,
          status,
          priority,
          startDate: startDate || undefined,
          dueDate: dueDate || undefined,
          effortDays: effortDays ? Number(effortDays) : undefined,
          notes: notes.trim() || undefined,
          nextAction: nextAction.trim() || undefined,
          advancementCriterion: advancement.trim() || undefined,
        });
        if (!res.success) throw new Error(res.error || res.message || 'Could not create the task.');
      }
      onSaved();
      onOpenChange(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit item' : 'Add to plan'}</DialogTitle>
          <DialogDescription>
            {locked
              ? lockedDescription(task?.externalSource)
              : 'Items added here live alongside the imported plan and are never overwritten by a re-import.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="pm-name">Name</Label>
            <Input
              id="pm-name"
              value={name}
              disabled={locked}
              placeholder="e.g. Submit Florida Medicaid enrollment"
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Workstream</Label>
              <Select value={workstreamId} onValueChange={setWorkstreamId} disabled={locked}>
                <SelectTrigger><SelectValue placeholder="Select a workstream" /></SelectTrigger>
                <SelectContent>
                  {workstreams.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Phase</Label>
              <Select value={phaseId} onValueChange={setPhaseId} disabled={locked || !phases.length}>
                <SelectTrigger><SelectValue placeholder={phases.length ? 'Optional' : 'Pick a workstream first'} /></SelectTrigger>
                <SelectContent>
                  {phases.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label>Owner</Label>
              <Select value={ownerRole} onValueChange={setOwnerRole} disabled={locked || isEdit}>
                <SelectTrigger><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent>
                  {owners.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select value={status} onValueChange={setStatus} disabled={locked}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Priority</Label>
              <Select value={priority} onValueChange={setPriority} disabled={locked}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label htmlFor="pm-start">Start</Label>
              <Input id="pm-start" type="date" value={startDate} disabled={locked} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pm-due">Due</Label>
              <Input id="pm-due" type="date" value={dueDate} disabled={locked} onChange={(e) => setDueDate(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pm-effort">Effort (days)</Label>
              <Input id="pm-effort" type="number" min="0" step="0.5" value={effortDays} disabled={locked} onChange={(e) => setEffortDays(e.target.value)} />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="pm-notes">Notes</Label>
            <Textarea id="pm-notes" rows={2} value={notes} disabled={locked} onChange={(e) => setNotes(e.target.value)} />
          </div>

          <div className="rounded-md border border-border bg-muted/40 p-3 space-y-4">
            <p className="text-xs text-muted-foreground">
              These two are always yours to set, on any item. An item with no next action is
              reported as an incomplete project state rather than as quietly fine.
            </p>
            <div className="space-y-1.5">
              <Label htmlFor="pm-next">Next action</Label>
              <Input
                id="pm-next"
                value={nextAction}
                placeholder="What must happen next for this to advance?"
                onChange={(e) => setNextAction(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pm-adv">Advancement criterion</Label>
              <Input
                id="pm-adv"
                value={advancement}
                placeholder="How will we know it moved? e.g. PTAN issued by CMS"
                onChange={(e) => setAdvancement(e.target.value)}
              />
            </div>
          </div>

          {error ? (
            <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-400">
              {error}
            </p>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving || !canSave} className="gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {isEdit ? 'Save changes' : 'Add to plan'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
